"use client";

import * as React from "react";
import { cn } from "@/utils/cn";
import { addDays, format, isValid, parseISO } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function DateRangePicker({
  className,
  value,
  onChange,
}: {
  className?: string;
  value?: DateRange;
  onChange?: (date: DateRange | undefined) => void;
}) {
  const [date, setDate] = React.useState<DateRange | undefined>(() => {
    if (!value?.from) return undefined;

    try {
      const from =
        value.from instanceof Date ? value.from : new Date(value.from);
      const to = value.to
        ? value.to instanceof Date
          ? value.to
          : new Date(value.to)
        : undefined;

      return isValid(from)
        ? { from, to: to && isValid(to) ? to : undefined }
        : undefined;
    } catch (e) {
      console.error("Error initializing dates:", e);
      return undefined;
    }
  });

  React.useEffect(() => {
    if (!value?.from) {
      setDate(undefined);
      return;
    }

    try {
      const from =
        value.from instanceof Date ? value.from : new Date(value.from);
      const to = value.to
        ? value.to instanceof Date
          ? value.to
          : new Date(value.to)
        : undefined;

      if (!isValid(from)) return;

      if (
        !date?.from ||
        from.getTime() !== date.from.getTime() ||
        (!date.to && to) ||
        (date.to && !to) ||
        (date.to && to && date.to.getTime() !== to.getTime())
      ) {
        setDate({
          from,
          to: to && isValid(to) ? to : undefined,
        });
      }
    } catch (e) {
      console.error("Error updating dates:", e);
    }
  }, [value, date]);

  const handleSelect = (newDate: DateRange | undefined) => {
    if (!newDate?.from) {
      setDate(undefined);
      onChange?.(undefined);
      return;
    }

    if (!isValid(newDate.from)) return;
    if (newDate.to && !isValid(newDate.to)) return;

    const range = {
      from: newDate.from,
      to: newDate.to,
    };

    setDate(range);
    onChange?.(range);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date || !isValid(date)) return null;
    return format(date, "LLL dd, y");
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {formatDate(date.from)} - {formatDate(date.to)}
                </>
              ) : (
                formatDate(date.from)
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={(date) =>
              date < new Date(new Date().setHours(0, 0, 0, 0))
            }
            showOutsideDays
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
